import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { SiteHeaderEn } from "@/components/SiteHeaderEn";
import { SiteFooterEn } from "@/components/SiteFooterEn";
import { LeadSection } from "@/components/LeadSection";

const SITE = "https://shopify-freelancer.net";
const TITLE = "Shopify Freelancer Services: Build, Develop and Improve";
const DESCRIPTION =
  "Shopify store builds, theme development, migrations and conversion work by freelance specialist Nicolai Schnepf. €110 net per hour or fixed scope.";

export const Route = createFileRoute("/en/services")({
  component: ServicesEn,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}/en/services` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/services` },
      { rel: "alternate", hrefLang: "de", href: "https://shopify-freelancer.net/leistungen" },
      { rel: "alternate", hrefLang: "en", href: "https://shopify-freelancer.net/en/services" },
      { rel: "alternate", hrefLang: "x-default", href: "https://shopify-freelancer.net/leistungen" },
      { rel: "alternate", hrefLang: "de", href: `${SITE}/leistungen` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}/en/services` },
    ],
  }),
});

const blocks = [
  {
    h: "New Shopify store",
    p: "A complete store from structure and theme to payments, shipping and launch. Fixed price after the scope is clear.",
    items: ["Store and catalogue structure", "Theme setup", "Products and collections", "Launch and handover"],
  },
  {
    h: "Theme development",
    p: "Sections, product-page functions, Liquid work and fixes inside your existing theme, without unnecessary apps.",
    items: ["Custom sections", "Liquid and metafields", "Bundles and variants", "Interfaces and error fixes"],
  },
  {
    h: "Conversion audit",
    p: "Analytics, session recordings, funnel and checkout data. You get a prioritised list of what costs you money, with the expected effect per item.",
    items: ["Funnel and drop-off analysis", "Session recordings", "Checkout diagnostics", "Priority list by revenue impact"],
  },
  {
    h: "Product page",
    p: "The page where the decision is made. Value proposition, variants, bundles and trust elements arranged around the buy button.",
    items: ["Above-the-fold structure", "Variant and bundle logic", "Trust and risk reversal", "Mobile-first layout"],
  },
  {
    h: "Checkout & cart",
    p: "Every unnecessary step is lost revenue. Shipping clarity, payment options and error states get fixed where they hurt.",
    items: ["Cart to checkout flow", "Shipping and payment clarity", "Form and error handling", "Recovery of abandoned carts"],
  },
  {
    h: "Page speed",
    p: "Slow stores lose mobile buyers before they see the product. Theme, apps and images are cleaned up against real Core Web Vitals.",
    items: ["App and script audit", "Theme and asset cleanup", "Image and font strategy", "Core Web Vitals verification"],
  },
  {
    h: "A/B testing",
    p: "Where the traffic supports it, the change is proven instead of argued. Where it does not, we ship the better-reasoned version.",
    items: ["Hypothesis from data", "Clean test setup", "Readable results", "Roll-out or roll-back"],
  },
  {
    h: "WooCommerce to Shopify",
    p: "Migrations without losing rankings: products, variants, customers and orders move over, URLs keep working through redirects.",
    items: ["Data migration", "Redirect map", "Theme rebuild", "Post-launch checks"],
  },
];

function ServicesEn() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeaderEn />
      <main>
        <section className="veil border-b border-border/60 py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Services
            </p>
            <h1 className="mt-5 max-w-3xl text-4xl font-bold leading-[1.08] md:text-5xl">
               The Shopify work your store <span className="text-signal">actually needs</span>
            </h1>
            <p className="mt-5 max-w-xl text-muted-foreground">
               Build a new store, solve a technical problem or improve sales from existing traffic.
               You work directly with the person doing the work. €110 net per hour or a fixed price
               for a clearly defined project.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto grid max-w-6xl gap-6 px-5 md:grid-cols-2">
            {blocks.map((b) => (
              <div key={b.h} className="rounded-lg border border-border bg-card p-6">
                <h2 className="text-xl font-semibold">{b.h}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.p}</p>
                <ul className="mt-4 grid gap-2 text-sm">
                  {b.items.map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <LeadSection source="en-services" lang="en" />
      </main>
      <SiteFooterEn />
    </div>
  );
}
